import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card, { SectionHeading } from "../../components/Card";
import CoreLayout from "../../components/layout/CoreLayout";
import Modal from "../../components/Modal";
import PageContainer from "../../components/PageContainer";
import PageHeader from "../../components/PageHeader";
import { cardBorderClassName } from "../../lib/cardStyles";
import {
  type CallDetail,
  type CallLog,
  parseCallDetail,
  parseCallLogDate,
  parseCallLogTotal,
  parseCallLogs,
} from "../../lib/callLogs";
import { formatDateTime } from "../../lib/formatDateTime";
import { formatPhoneDisplay } from "../../lib/formatPhone";
import { usePageTitle } from "../../lib/usePageTitle";
import { handleAuthHttpStatus, isAuthHttpStatus } from "../../lib/authErrors";

const API_BASE =
  import.meta.env.VITE_API_URL || "https://app.neurospherevoiceai.com/api";

const filters = ["Today", "Last 7 days", "Last 30 days"] as const;
type Filter = (typeof filters)[number];

function getCustomerId(): string | null {
  return localStorage.getItem("customerId");
}

/** Strip non-digits; compare on trailing 10 for US numbers. */
function phoneTailDigits(phone: string): string {
  const d = phone.replace(/\D/g, "");
  return d.length >= 10 ? d.slice(-10) : d;
}

function logMatchesSearch(log: CallLog, query: string): boolean {
  const q = query.trim();
  if (!q) return true;

  const qLower = q.toLowerCase();
  if (log.caller.toLowerCase().includes(qLower)) return true;
  if (log.summary.toLowerCase().includes(qLower)) return true;

  const qDigits = q.replace(/\D/g, "");
  if (qDigits.length === 0) return false;

  const callerDigits = log.caller.replace(/\D/g, "");
  if (qDigits.length >= 10) {
    return phoneTailDigits(callerDigits) === phoneTailDigits(qDigits);
  }
  return callerDigits.includes(qDigits);
}

function isWithinFilter(dateStr: string, filter: Filter): boolean {
  const d = parseCallLogDate(dateStr);
  if (!d) return true;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (filter === "Today") return d >= startOfToday;
  if (filter === "Last 7 days") {
    const cutoff = new Date(startOfToday);
    cutoff.setDate(cutoff.getDate() - 6);
    return d >= cutoff;
  }
  const cutoff = new Date(startOfToday);
  cutoff.setDate(cutoff.getDate() - 29);
  return d >= cutoff;
}

function formatCallLogDate(dateStr: string): string {
  return formatDateTime(dateStr) || "—";
}

function formatDetailDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} sec`;
  return `${Math.round(seconds / 60)} min`;
}

const detailFont = { fontFamily: "'Space Grotesk', sans-serif" };

function formatTurnTime(occurredAt: string | null): string | null {
  if (!occurredAt) return null;
  const parsed = parseCallLogDate(occurredAt);
  if (!parsed) return null;
  return parsed.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

type DetailView =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "ready"; detail: CallDetail }
  | { phase: "not_found" }
  | { phase: "unavailable" }
  | { phase: "error" }
  | { phase: "unauthorized" };

function DetailRetryButton({ onRetry }: { onRetry: () => void }) {
  return (
    <button
      type="button"
      onClick={onRetry}
      className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer"
      style={{ background: "linear-gradient(90deg, #8a2be2, #ff6a00)" }}
    >
      Retry
    </button>
  );
}

function DetailMessage({
  children,
  retry,
}: {
  children: string;
  retry?: () => void;
}) {
  return (
    <div>
      <p className="text-sm text-gray-500 mb-3" style={detailFont}>
        {children}
      </p>
      {retry ? <DetailRetryButton onRetry={retry} /> : null}
    </div>
  );
}

// TODO: remove isRawContextDump / cleanSummary once the backend writes real summaries (not raw context blobs).
const isRawContextDump = (s?: string) => {
  if (!s) return false;
  const t = s.trim();
  return /^Call SID:/i.test(t) || /Runtime identity/i.test(t) || /={6,}/.test(t);
};
const cleanSummary = (s?: string) => (isRawContextDump(s) ? "" : (s ?? ""));

const FILTER_WINDOW_LABELS: Record<Filter, string> = {
  Today: "today",
  "Last 7 days": "the last 7 days",
  "Last 30 days": "the last 30 days",
};

function CallLogSkeletonRow() {
  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg ${cardBorderClassName}`}
      style={{ background: "rgba(13, 13, 18, 0.6)" }}
      aria-hidden
    >
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <div className="h-4 w-32 rounded bg-white/10 animate-pulse" />
          <div className="h-3 w-24 rounded bg-white/10 animate-pulse" />
        </div>
        <div className="h-3 w-full max-w-md rounded bg-white/10 animate-pulse" />
      </div>
      <div className="h-9 w-28 shrink-0 rounded-lg bg-white/10 animate-pulse" />
    </div>
  );
}

function SectionCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
}) {
  return (
    <Card padding="p-5">
      <SectionHeading icon={icon} className="mb-5">
        {title}
      </SectionHeading>
      {children}
    </Card>
  );
}

function CallTranscript({ view, onRetry }: { view: DetailView; onRetry: () => void }) {
  if (view.phase === "idle") return null;

  if (view.phase === "loading") {
    return (
      <div className="space-y-2" aria-hidden>
        <div className="h-3 w-full max-w-md rounded bg-white/10 animate-pulse" />
        <div className="h-3 w-full max-w-md rounded bg-white/10 animate-pulse" />
        <div className="h-3 w-full max-w-md rounded bg-white/10 animate-pulse" />
      </div>
    );
  }

  if (view.phase === "not_found") {
    return <DetailMessage>This call could not be found.</DetailMessage>;
  }

  if (view.phase === "unavailable") {
    return (
      <DetailMessage retry={onRetry}>
        Call details are temporarily unavailable.
      </DetailMessage>
    );
  }

  if (view.phase === "unauthorized") {
    return <DetailMessage>Unauthorized — please log in again.</DetailMessage>;
  }

  if (view.phase === "error") {
    return <DetailMessage retry={onRetry}>Could not load this call.</DetailMessage>;
  }

  const visibleTurns = view.detail.turns.filter((turn) => turn.role !== "system");

  return (
    <div>
      {view.detail.status === "in_progress" && (
        <p className="text-sm text-gray-500 mb-4" style={detailFont}>
          Call in progress. Transcript may be incomplete.
        </p>
      )}
      {visibleTurns.length === 0 ? (
        <p className="text-sm text-gray-500" style={detailFont}>
          No transcript for this call.
        </p>
      ) : (
        <div>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Transcript
          </h3>
          <div className="space-y-3">
            {visibleTurns.map((turn, index) => {
              const isUser = turn.role === "user";
              const time = formatTurnTime(turn.occurred_at);
              return (
                <div
                  key={`${turn.sequence}-${index}`}
                  className={`flex ${isUser ? "justify-start" : "justify-end"}`}
                >
                  <div className={`max-w-[85%] flex flex-col ${isUser ? "items-start" : "items-end"}`}>
                    <div className="mb-1 flex items-baseline gap-2">
                      <span className="text-xs font-semibold text-gray-400" style={detailFont}>
                        {isUser ? "Caller" : "AI receptionist"}
                      </span>
                      {time ? (
                        <span className="text-xs text-gray-500" style={detailFont}>
                          {time}
                        </span>
                      ) : null}
                    </div>
                    <p
                      className={`text-sm text-white/90 whitespace-pre-wrap rounded-lg px-3 py-2 ${
                        isUser ? "bg-white/5" : "bg-purple-500/10"
                      }`}
                      style={detailFont}
                    >
                      {turn.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CallLogs() {
  usePageTitle("Call Logs");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingSidRef = useRef(searchParams.get("sid"));

  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<Filter>(() =>
    pendingSidRef.current ? "Last 30 days" : "Today"
  );
  const [allLogs, setAllLogs] = useState<CallLog[]>([]);
  const [callTotal, setCallTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<CallLog | null>(null);
  const [detailView, setDetailView] = useState<DetailView>({ phase: "idle" });
  const [detailAttempt, setDetailAttempt] = useState(0);
  const lastRowButtonRef = useRef<HTMLElement | null>(null);

  const closeDetail = () => {
    setSelectedLog(null);
    setDetailView({ phase: "idle" });
  };

  const openDetail = (log: CallLog, rowButton?: HTMLElement | null) => {
    lastRowButtonRef.current = rowButton ?? null;
    setSelectedLog(log);
    setDetailView(log.call_sid ? { phase: "loading" } : { phase: "idle" });
  };

  const fetchLogs = useCallback(async () => {
    const customerId = getCustomerId();
    if (!customerId) {
      setError("No customer ID found. Please log in again.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/call-logs?limit=500`,
        { method: "GET", credentials: "include" }
      );

      if (isAuthHttpStatus(res.status)) {
        void handleAuthHttpStatus(res.status, () => {
          setError("Unauthorized — please log in again.");
        });
        return;
      }

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        setError(
          `Failed to load call logs (HTTP ${res.status})${text ? `: ${text.slice(0, 120)}` : ""}`
        );
        return;
      }

      const data = await res.json();
      setAllLogs(parseCallLogs(data));
      setCallTotal(parseCallLogTotal(data));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Network error while loading call logs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    const sid = pendingSidRef.current;
    if (!sid || loading) return;
    pendingSidRef.current = null;
    const match = allLogs.find((log) => log.call_sid === sid);
    openDetail(
      match ?? { call_sid: sid, date: "", caller: "", summary: "", duration_seconds: undefined }
    );
    setSearchParams({}, { replace: true });
  }, [allLogs, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCallSid = selectedLog?.call_sid ?? "";

  useEffect(() => {
    if (!selectedCallSid) {
      setDetailView({ phase: "idle" });
      return;
    }

    const controller = new AbortController();
    let active = true;
    setDetailView({ phase: "loading" });

    const load = async () => {
      try {
        const customerId = getCustomerId();
        if (!customerId) {
          if (active) setDetailView({ phase: "error" });
          return;
        }

        const res = await fetch(
          `${API_BASE}/customers/${customerId}/call-logs/${encodeURIComponent(selectedCallSid)}`,
          { method: "GET", credentials: "include", signal: controller.signal },
        );
        if (!active) return;

        if (isAuthHttpStatus(res.status)) {
          let reported = false;
          await handleAuthHttpStatus(res.status, () => {
            reported = true;
          });
          if (!active) return;
          setDetailView(reported ? { phase: "unauthorized" } : { phase: "idle" });
          return;
        }

        if (res.status === 404) {
          setDetailView({ phase: "not_found" });
          return;
        }
        if (res.status === 503) {
          setDetailView({ phase: "unavailable" });
          return;
        }
        if (!res.ok) {
          setDetailView({ phase: "error" });
          return;
        }

        let data: unknown;
        try {
          data = await res.json();
        } catch {
          if (active) setDetailView({ phase: "error" });
          return;
        }
        if (!active) return;
        setDetailView({ phase: "ready", detail: parseCallDetail(data) });
      } catch (e: unknown) {
        if (!active) return;
        if (e instanceof Error && e.name === "AbortError") return;
        setDetailView({ phase: "error" });
      }
    };

    void load();

    return () => {
      active = false;
      controller.abort();
    };
  }, [selectedCallSid, detailAttempt]);

  useEffect(() => {
    if (!loading && activeFilter === "Today") {
      const todayCount = allLogs.filter((log) => isWithinFilter(log.date, "Today")).length;
      if (todayCount === 0) setActiveFilter("Last 30 days");
    }
  }, [loading, activeFilter, allLogs]);

  const visibleLogs = allLogs.filter((log) => {
    const q = searchQuery.trim();
    if (q) return logMatchesSearch(log, q);
    return isWithinFilter(log.date, activeFilter);
  });

  return (
    <CoreLayout>
      <PageContainer>
        <PageHeader
          title="Call Logs"
          subtitle="Every conversation your AI has handled — searchable and reviewable."
        />

        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by phone or summary"
              aria-label="Search calls by phone number or summary"
              className="w-full md:max-w-md px-4 py-3 rounded-lg bg-black/40 border border-purple-500/30 text-white text-sm focus:outline-none focus:border-purple-500"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            />

            <div className="inline-flex shrink-0 rounded-lg bg-white/5 p-1 border border-white/10">
              {filters.map((filter) => {
                const isActive = activeFilter === filter;
                return (
                  <button
                    key={filter}
                    type="button"
                    onClick={() => setActiveFilter(filter)}
                    className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap transition-colors cursor-pointer ${
                      isActive
                        ? "bg-[#8a2be2] text-white"
                        : "text-gray-400 hover:text-gray-200"
                    }`}
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {filter}
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <div
              className="p-4 rounded-xl text-center"
              style={{
                background: "rgba(220, 38, 38, 0.12)",
                border: "1px solid rgba(248, 113, 113, 0.35)",
              }}
            >
              <p
                className="text-red-300 text-sm mb-3"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {error}
              </p>
              <button
                type="button"
                onClick={fetchLogs}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer"
                style={{ background: "linear-gradient(90deg, #8a2be2, #ff6a00)" }}
              >
                Retry
              </button>
            </div>
          )}

          <SectionCard title="Recent Calls" icon="ri-phone-line">
            {!error && !loading && callTotal !== null && callTotal > allLogs.length ? (
              <p
                className="text-sm text-gray-500 mb-3"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Showing the most recent {allLogs.length} of {callTotal} calls.
              </p>
            ) : null}
            {error ? null : loading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }, (_, i) => (
                  <CallLogSkeletonRow key={i} />
                ))}
              </div>
            ) : visibleLogs.length === 0 ? (
              <div className="text-center py-16 max-w-md mx-auto">
                {allLogs.length === 0 ? (
                  <>
                    <i className="ri-phone-line text-5xl text-purple-400/30 mb-5 block" />
                    <h3
                      className="text-lg font-semibold text-white mb-3"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      No calls yet
                    </h3>
                    <div className="flex items-center justify-center gap-2">
                      <span className="relative flex h-2.5 w-2.5 shrink-0">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
                      </span>
                      <p
                        className="text-sm text-gray-400"
                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                      >
                        Your AI is actively waiting for calls
                      </p>
                    </div>
                  </>
                ) : searchQuery.trim() ? (
                  <p
                    className="text-sm text-gray-400"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    No calls match &ldquo;{searchQuery.trim()}&rdquo;.
                  </p>
                ) : (
                  <p
                    className="text-sm text-gray-400"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    No calls in {FILTER_WINDOW_LABELS[activeFilter]}.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {visibleLogs.map((log) => (
                  <div
                    key={log.call_sid || log.date + log.caller}
                    className={`group flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-lg hover:bg-white/5 ${cardBorderClassName}`}
                    style={{
                      background: "rgba(13, 13, 18, 0.6)",
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => openDetail(log, e.currentTarget)}
                      className="flex-1 min-w-0 text-left cursor-pointer rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8a2be2]"
                    >
                      <div className="flex flex-wrap items-center gap-3 mb-1">
                        <span className="font-mono text-sm font-semibold text-white">
                          {formatPhoneDisplay(log.caller) || "Unknown caller"}
                        </span>
                        <span
                          className="text-xs text-gray-500"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                          {formatCallLogDate(log.date)}
                        </span>
                        <i
                          className="ri-arrow-right-s-line text-gray-600 group-hover:text-gray-300 ml-auto"
                          aria-hidden
                        />
                      </div>
                      {(() => {
                        const summary = cleanSummary(log.summary).trim();
                        if (summary) {
                          return (
                            <p
                              className="text-gray-400 text-sm truncate"
                              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                              title={summary}
                            >
                              {summary.length > 100 ? `${summary.slice(0, 100)}…` : summary}
                            </p>
                          );
                        }
                        const seconds = log.duration_seconds ?? 0;
                        if (!(seconds > 0)) return null;
                        return (
                          <p
                            className="text-gray-500 text-sm"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                          >
                            {formatDetailDuration(seconds)}
                          </p>
                        );
                      })()}
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/user-memories?phone=${encodeURIComponent(log.caller)}`);
                      }}
                      className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm text-gray-300 border border-white/10 hover:border-[#8a2be2]/50 hover:text-white cursor-pointer"
                    >
                      Profile
                      <i className="ri-arrow-right-s-line" aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      </PageContainer>
      <Modal
        open={!!selectedLog}
        onClose={closeDetail}
        title="Call detail"
        subtitle={
          selectedLog
            ? [
                selectedLog.caller ? formatPhoneDisplay(selectedLog.caller) : "",
                formatDateTime(selectedLog.date),
              ]
                .filter(Boolean)
                .join(" · ") || undefined
            : undefined
        }
        returnFocusTo={lastRowButtonRef}
      >
        {selectedLog && (
          <>
            {(selectedLog.duration_seconds ?? 0) > 0 && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-400">
                  {formatDetailDuration(selectedLog.duration_seconds ?? 0)}
                </span>
              </div>
            )}
            {cleanSummary(selectedLog.summary).trim() ? (
              <div className="mb-4">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Summary</h3>
                <p className="text-sm text-white/90 leading-relaxed">{cleanSummary(selectedLog.summary)}</p>
              </div>
            ) : null}
            {selectedLog.call_sid ? (
              <div className="mb-4">
                <CallTranscript
                  view={detailView}
                  onRetry={() => setDetailAttempt((n) => n + 1)}
                />
              </div>
            ) : null}
          </>
        )}
      </Modal>
    </CoreLayout>
  );
}
